<template>
    <!-- Desktop: vertical dots on right edge -->
    <nav
        class="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-50 flex-col items-end gap-5"
        aria-label="Page sections"
    >
        <button
            v-for="section in SECTIONS"
            :key="section.id"
            @click="scrollTo(section.id)"
            @mouseenter="hoveredDot = section.id"
            @mouseleave="hoveredDot = null"
            class="flex items-center gap-3 group"
            :aria-label="`Navigate to ${section.label}`"
        >
            <Transition name="label">
                <span
                    v-if="hoveredDot === section.id"
                    class="text-[11px] font-medium text-white/70 whitespace-nowrap pointer-events-none"
                >{{ section.label }}</span>
            </Transition>

            <div class="relative flex items-center justify-center w-4 h-4">
                <div :class="[
                    'w-3 h-3 rounded-full transition-all duration-300',
                    activeSection === section.id
                        ? 'scale-100 opacity-100 bg-violet-400'
                        : 'scale-[0.6] opacity-30 bg-white/60 group-hover:bg-white/80'
                ]" />
                <div
                    v-if="activeSection === section.id"
                    class="absolute inset-[-4px] rounded-full border border-violet-400/40"
                />
            </div>
        </button>
    </nav>

    <!-- Mobile: horizontal dots at bottom center -->
    <nav
        class="flex lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-50 items-center gap-1 px-3 py-2.5 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-xl"
        aria-label="Page sections"
    >
        <button
            v-for="section in SECTIONS"
            :key="section.id"
            @click="scrollTo(section.id)"
            class="relative flex items-center justify-center p-1.5"
            :aria-label="`Navigate to ${section.label}`"
        >
            <div :class="[
                'w-2 h-2 rounded-full transition-all duration-300',
                activeSection === section.id
                    ? 'scale-100 opacity-100 bg-violet-400'
                    : 'scale-[0.65] opacity-30 bg-white/60'
            ]" />
            <div
                v-if="activeSection === section.id"
                class="absolute inset-0.5 rounded-full border border-violet-400/40"
            />
        </button>
    </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const SECTIONS = [
    { id: 'top',           label: 'Home' },
    { id: 'how-it-works',  label: 'How It Works' },
    { id: 'pitch-showcase', label: 'Pitches' },
    { id: 'pitch-form',    label: 'Create' },
]

const activeSection = ref('top')
const hoveredDot    = ref(null)
const observers     = []

function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

onMounted(() => {
    SECTIONS.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) activeSection.value = id },
            { threshold: 0.3, rootMargin: '-10% 0px -10% 0px' }
        )
        observer.observe(el)
        observers.push(observer)
    })
})

onUnmounted(() => observers.forEach(o => o.disconnect()))
</script>

<style scoped>
.label-enter-active, .label-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.label-enter-from, .label-leave-to       { opacity: 0; transform: translateX(8px); }
</style>
